import React, { useEffect, useMemo, useState } from 'react';
import { Popover, Select, Tooltip } from 'antd';
import { GlobalOutlined } from '@ant-design/icons';
import './index.less';
import { useSearchParams } from 'react-router-dom';
import {
  ActionCreatorWithPayload,
  AnyAction,
  Dispatch,
} from '@reduxjs/toolkit';
const { Option } = Select;

interface Props {
  languagesArr: {
    code: string;
    dir: 'ltr' | 'rtl';
    name: string;
  }[];
  defaultLang: string | null;
  dispatch?: Dispatch<AnyAction>;
  changeLocale: (lang: string) => void;
  withoutBorders?: boolean;
  smallView?: boolean;
  wrapperClassName?: string;
  globeTooltip?: string;
}

const SiteLang: React.FC<Props> = ({
  languagesArr,
  defaultLang,
  dispatch,
  changeLocale,
  withoutBorders,
  smallView = false,
  wrapperClassName,
  globeTooltip,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [selectInputValue, setSelectInputValue] = useState(
    defaultLang ?? 'he_IL'
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 750px)');
    setIsMobile(mq.matches);
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const handleLangChange = async (lang: string) => {
    changeLocale(lang);
    localStorage.setItem('lang', lang);
    if (searchParams.get('lang')) {
      searchParams.set('lang', lang);
      setSearchParams(searchParams);
    }
    setSelectInputValue(lang);
    setPopoverOpen(false);
  };

  const filteredLangs = useMemo(() => {
    return languagesArr.filter(
      (lang) => !!lang?.dir && !!lang?.code && !!lang?.name
    );
  }, [languagesArr]);

  const mobileContent = (
    <div className="mobile-lang-options">
      {filteredLangs.map((item) => (
        <div
          key={item.code}
          className={`mobile-lang-option ${selectInputValue === item.code ? 'active' : ''}`}
          onClick={() => handleLangChange(item.code)}
        >
          {item.name}
        </div>
      ))}
    </div>
  );

  if (isMobile) {
    return (
      <Popover
        content={mobileContent}
        open={popoverOpen}
        onOpenChange={setPopoverOpen}
        trigger="click"
        title={globeTooltip}
      >
        <Tooltip title={globeTooltip}>
          <div
            className={`site-lang mobile-globe ${smallView ? 'smallView' : ''} ${wrapperClassName || ''}`}
          >
            <GlobalOutlined className={'language-selector-globe'} />
          </div>
        </Tooltip>
      </Popover>
    );
  }

  return (
    <div
      className={`site-lang ${smallView ? 'smallView' : ''} ${
        wrapperClassName || ''
      }`}
      onClick={() => setIsDropdownOpen((prev) => !prev)}
    >
      <Tooltip title={globeTooltip}>
        <GlobalOutlined className={'language-selector-globe'} />
      </Tooltip>
      {smallView && (
        <span className="lang-code">{selectInputValue?.split('_')[1]}</span>
      )}
      <Select
        className={`language-selector ${smallView ? 'smallView' : ''}`}
        value={selectInputValue}
        onChange={(value) => {
          handleLangChange(value);
        }}
        open={isDropdownOpen}
        onBlur={() => setIsDropdownOpen(false)}
        bordered={!withoutBorders}
      >
        {filteredLangs &&
          filteredLangs.map((item) => {
            if (!item?.code) {
              return null;
            }
            return (
              <Option key={`${item?.code}-${item?.name}`} value={item?.code}>
                {item?.name}
              </Option>
            );
          })}
      </Select>
    </div>
  );
};

export default SiteLang;
